/* eslint-disable max-statements, max-len */
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Formik } from "formik";
import PropTypes from "prop-types";

import { mfaValidationSchema, useAuth } from "../services/eqqo.auth.service";
import loginMFACode from "../sign-up/getMFACodesMutation";
import validateMFACode from "../sign-up/validateMFACodesMutation";

const revalidateTimeout = 15 * 60 * 1000;
const resendCodeTimeout = 5 * 60 * 1000;

const RevalidateMFAWrapper = ({ children }) => {
	const { login, user } = useAuth();
	const { mutateAsync: resendMFACode } = loginMFACode(useMutation);
	const { mutateAsync: validateCode } = validateMFACode(useMutation);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [successMessage, setSuccessMessage] = useState(null);
	const inputRef = useRef();
	const queryClient = useQueryClient();

	const sendNewCode = useCallback(async () => {

		if ( !user || !user.email ) {
			return;
		}

		setLoading(true);
		setErrorMessage(null);

		const input = {
			username: user.email
		};

		try {
			await resendMFACode(input);
			setSuccessMessage("MFA code sent successfully. Please check your email.");
			window.localStorage.setItem("lastMFACodeRequestTS", Date.now());
		} catch (error) {
			/* eslint-disable no-console */
			console.log(error);
			setErrorMessage(error.message);

			inputRef?.current?.select();
		}
		
		setLoading(false);
	}, [login, queryClient, user]);
	useEffect(() => {
		const lastMFACodeRequestTS = window.localStorage.getItem("lastMFACodeRequestTS");
		if (!lastMFACodeRequestTS || Date.now() - lastMFACodeRequestTS > resendCodeTimeout) {
			sendNewCode();
		}
	}, [resendMFACode, sendNewCode]);

	const handleFormSubmit = useCallback(async (values) => {
		setLoading(true);
		setErrorMessage(null);

		const input = {
			mfaCode: values.signInCode
		};

		try {
			await validateCode(input);
			await queryClient.refetchQueries('auth-user');
		} catch (error) {
			/* eslint-disable no-console */
			console.log(error);
			setErrorMessage(error.message);

			inputRef?.current?.select();
		}
	}, [user, validateCode, queryClient]);


	const handleResendCode = useCallback(async () => {
		setLoading(true);
		// Logic to resend the MFA code
		try {
			await resendMFACode({ username: user.email });
			setSuccessMessage("MFA code resent successfully. Please check your email.");
			setErrorMessage(null);
			inputRef.current.select();
		} catch (error) {
			setErrorMessage(error.message);
		}

		setLoading(false);
	}, [user, resendMFACode, queryClient]);

	return user?.mfaLastUsed && parseInt(user?.mfaLastUsed, 10) * 1000 > Date.now() - revalidateTimeout
		? children
		: (
			<>
				{errorMessage
					? (
						<Alert className="my-3 d-flex align-items-center" variant="danger">
							<span className="flex-fill">{errorMessage}</span>
							<Button
								disabled={loading}
								onClick={handleResendCode}
								variant="danger"
							>
								{loading ? <Spinner animation="border" size="sm" /> : "Resend Code"}
							</Button>
						</Alert>
					)
					: null}

				{successMessage
					? (
						<Alert className="my-3 d-flex align-items-center" variant="success">
							<span className="flex-fill">{successMessage}</span>
						</Alert>
					)
					: null}
				<Formik
					initialValues={{
						signInCode: ""
					}}
					onSubmit={handleFormSubmit}
					validationSchema={mfaValidationSchema}
				>
					{({
						errors,
						handleBlur,
						handleChange,
						handleSubmit,
						touched,
						values
					}) => (
						<Form onSubmit={handleSubmit}>
							<Row>
								<Col
									className="align-items-end"
									lg={9}
									md={6}
									sm={12}
									xs={12}
								>
									<Form.Group controlId="mfaCode">
										<Form.Label>Enter MFA Code</Form.Label>
										<Form.Control
											isInvalid={Boolean(errors.signInCode)}
											maxLength="6"
											name="signInCode"
											onBlur={handleBlur}
											onChange={handleChange}
											ref={inputRef}
											required
											type="text"
											value={values?.signInCode}
										/>
									</Form.Group>
									<Form.Control.Feedback type="invalid">
										{touched.signInCode && errors.signInCode ? <p className="my-0">{errors.signInCode}</p> : null}
									</Form.Control.Feedback>
								</Col>
								<Col className="d-flex align-items-end">
									<Button className="btn-gradient w-100" type="submit">
										{loading ? <Spinner animation="border" size="sm" /> : "Verify Code"}
									</Button>
								</Col>
							</Row>
							<p className="mt-1">Please enter the 6-digit MFA code sent to your device. <Button className="d-inline m-0 p-0" onClick={handleResendCode} variant="link">Resend Code.</Button></p>
						</Form>
					)}
				</Formik>
			</>
		);
};

RevalidateMFAWrapper.propTypes = {
	children: PropTypes.node.isRequired,
};

export default RevalidateMFAWrapper;